import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { RenderTabContentBox } from './styled/index';
import StopsTable from './StopsTable';
import { RouteWithDetailedView, StopDetailedView } from './types';
import { SnackbarSeverityType } from '../../enums';
import DropdownSelect from '../../components/DropdownSelect';
import { DropdownSelectOption } from '../../components/DropdownSelect/DropdownSelect';
import AddStopDialog from './components/AddStopDialog';
import EditStopDialog from './components/EditStopDialog';
import AssignStudentDialog from './components/AssignStudentDialog';
import { getStops, deleteStop } from '../../apis/transport/stops';
import { getRoutes } from '../../apis/transport/routes';
import DialogModal from '../../components/DialogModal';
import { User } from '../../types/userType';
import pallet from '../../layouts/pallet';
import { PaperWrapper } from '../common/styled';
import { ButtonContainer } from '../Exams/styled';

interface StopsContainerProps {
  showToast: (message: string, severity: SnackbarSeverityType) => void;
  loggedInUser: User;
}

const StopsContainer: React.FC<StopsContainerProps> = ({
  showToast,
  loggedInUser
}) => {
  const [loading, setLoading] = useState(false);
  const [stops, setStops] = useState<StopDetailedView[]>([]);
  const [routes, setRoutes] = useState<RouteWithDetailedView[]>([]);
  const [selectedRoute, setSelectedRoute] = useState<string>('');
  const [showStopsTable, setShowStopsTable] = useState(false);
  const [isAddStopDialogOpen, setIsAddStopDialogOpen] = useState(false);
  const [isEditStopDialogOpen, setIsEditStopDialogOpen] = useState(false);
  const [currentStopToEdit, setCurrentStopToEdit] =
    useState<StopDetailedView | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [stopToDelete, setStopToDelete] = useState<StopDetailedView | null>(
    null
  );
  const [isAssignStudentDialogOpen, setIsAssignStudentDialogOpen] =
    useState(false);
  const [currentStopForStudentAssignment, setCurrentStopForStudentAssignment] =
    useState<StopDetailedView | null>(null);

  const fetchStops = useCallback(
    async (routeId?: string) => {
      if (loggedInUser && routeId) {
        setLoading(true);
        try {
          const stopsData = await getStops(loggedInUser.school, routeId, true);
          setStops(stopsData as unknown as StopDetailedView[]);
          setShowStopsTable(true);
        } catch (error) {
          showToast('Failed to fetch stops', SnackbarSeverityType.error);
        } finally {
          setLoading(false);
        }
      }
    },
    [loggedInUser, showToast]
  );

  const fetchRoutes = useCallback(async () => {
    if (loggedInUser) {
      setLoading(true);
      try {
        const response = await getRoutes(loggedInUser.school);
        const routesData = response as unknown as RouteWithDetailedView[];
        setRoutes(routesData);
      } catch (error) {
        showToast('Error fetching routes', SnackbarSeverityType.error);
      } finally {
        setLoading(false);
      }
    }
  }, [loggedInUser, showToast]);

  useEffect(() => {
    if (loggedInUser?.school) {
      fetchRoutes();
    }
  }, [loggedInUser?.school, fetchRoutes]);

  const handleStopAdded = () => {
    fetchStops(selectedRoute);
  };

  const handleStopUpdated = () => {
    fetchStops(selectedRoute);
  };

  const handleDeleteStop = (stop: StopDetailedView) => {
    setStopToDelete(stop);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (stopToDelete) {
      try {
        await deleteStop(selectedRoute, stopToDelete.id);
        showToast(
          `Stop: ${stopToDelete.name} deleted.`,
          SnackbarSeverityType.warning
        );
        fetchStops(selectedRoute);
      } catch (error) {
        showToast('Failed to delete stop', SnackbarSeverityType.error);
      } finally {
        setIsDeleteDialogOpen(false);
        setStopToDelete(null);
      }
    }
  };

  const routeOptions: DropdownSelectOption[] = routes.map((route) => ({
    value: route.id,
    label: route.name
  }));

  return (
    <>
      <PaperWrapper>
        <RenderTabContentBox>
          <Box display='flex' alignItems='flex-end' gap={4}>
            <DropdownSelect
              label='Select Route'
              placeholder='Select Route'
              name='selectRoute'
              value={selectedRoute}
              options={routeOptions}
              onChange={(e) => {
                setSelectedRoute(e.target.value as string);
                setStops([]);
                setShowStopsTable(false);
              }}
              hasTopLabel
              sx={{ width: '250px' }}
            />
            <ButtonContainer>
              <Button
                variant='contained'
                onClick={() => fetchStops(selectedRoute)}
                disabled={loading || !selectedRoute}
              >
                Search
              </Button>
            </ButtonContainer>
          </Box>
        </RenderTabContentBox>
      </PaperWrapper>

      {showStopsTable && (
        <PaperWrapper>
          <RenderTabContentBox>
            <Box display='flex' justifyContent='flex-end' sx={{ mb: 2 }}>
              <Button
                variant='contained'
                onClick={() => setIsAddStopDialogOpen(true)}
              >
                + Add A Stop
              </Button>
            </Box>
            <StopsTable
              stops={stops}
              loading={loading}
              onEdit={(stop: StopDetailedView) => {
                setCurrentStopToEdit(stop);
                setIsEditStopDialogOpen(true);
              }}
              onDelete={handleDeleteStop}
              onAssignStudent={(stop: StopDetailedView) => {
                setCurrentStopForStudentAssignment(stop);
                setIsAssignStudentDialogOpen(true);
              }}
            />
          </RenderTabContentBox>
        </PaperWrapper>
      )}

      <AddStopDialog
        open={isAddStopDialogOpen}
        onClose={() => setIsAddStopDialogOpen(false)}
        onStopAdded={handleStopAdded}
        stops={stops}
        loggedInUser={loggedInUser}
        showToast={showToast}
        selectedRouteId={selectedRoute}
      />
      {currentStopToEdit && (
        <EditStopDialog
          open={isEditStopDialogOpen}
          onClose={() => setIsEditStopDialogOpen(false)}
          onEditStop={handleStopUpdated}
          currentStop={currentStopToEdit}
          stops={stops}
          showToast={showToast}
        />
      )}

      {currentStopForStudentAssignment && (
        <AssignStudentDialog
          open={isAssignStudentDialogOpen}
          onClose={() => setIsAssignStudentDialogOpen(false)}
          onStudentsAssigned={handleStopUpdated}
          currentStop={currentStopForStudentAssignment}
          showToast={showToast}
          loggedInUser={loggedInUser}
        />
      )}
      <DialogModal
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        title='Delete Stop'
        actions={
          <>
            <Button
              onClick={() => setIsDeleteDialogOpen(false)}
              color='primary'
              variant='contained'
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              color='primary'
              variant='contained'
              autoFocus
              sx={{
                backgroundColor: pallet.unlinkRed,
                color: pallet.white,
                '&:hover': {
                  backgroundColor: pallet.unlinkRed
                }
              }}
            >
              Delete
            </Button>
          </>
        }
      >
        Are you sure you want to{' '}
        <Typography
          component='span'
          sx={{ color: pallet.unlinkRed, fontWeight: 'bold' }}
        >
          delete{' '}
        </Typography>
        the stop:{' '}
        <Typography
          component='span'
          sx={{ color: pallet.unlinkRed, fontWeight: 'bold' }}
        >
          {stopToDelete?.name}
        </Typography>
        ?
      </DialogModal>
    </>
  );
};

export default StopsContainer;
